/**
 * Legal Document queries using React Relay
 *
 * Provides access to the `currentLegalDocument` GraphQL query, which returns the
 * currently effective legal document for a system (falling back to the org or
 * Movatic-level doc if none is scoped to the system). This replaces the legacy
 * REST `GET /system/tos` endpoint for reading a system's Terms of Service.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useLegalDocumentQueriesCurrentQuery } from './__generated__/useLegalDocumentQueriesCurrentQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const CurrentLegalDocumentQuery = graphql`
  query useLegalDocumentQueriesCurrentQuery(
    $documentType: String!
    $locale: String!
    $systemId: ID!
  ) {
    currentLegalDocument(
      documentType: $documentType
      locale: $locale
      systemId: $systemId
    ) {
      id
      documentType
      locale
      version
      systemId
      organizationId
      effectiveFrom
      textContent
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface LegalDocumentGraphQL {
  id: string;
  documentType: string;
  locale: string;
  version: string;
  systemId?: string | null;
  organizationId?: string | null;
  effectiveFrom?: string | null;
  textContent: string;
}

export interface CurrentLegalDocumentVars {
  documentType: string;
  locale: string;
  systemId: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Suspense-based hook for fetching the currently effective legal document.
 */
export function useCurrentLegalDocument(vars: CurrentLegalDocumentVars) {
  const data = useLazyLoadQuery<useLegalDocumentQueriesCurrentQuery>(
    CurrentLegalDocumentQuery,
    vars
  );

  return data.currentLegalDocument as LegalDocumentGraphQL | null;
}

/**
 * React Query-like hook (no Suspense) for the currently effective legal document.
 */
export function useCurrentLegalDocumentQuery(
  vars: Partial<CurrentLegalDocumentVars> & { locale?: string },
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<LegalDocumentGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const documentType = vars.documentType ?? 'tos';
  const locale = vars.locale ?? 'en';
  const systemId = vars.systemId;
  const enabled = options?.enabled !== false && Boolean(systemId);

  useEffect(() => {
    if (!enabled || !systemId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchCurrentLegalDocument({
          documentType,
          locale,
          systemId,
        });

        if (cancelled) return;

        setData(result);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch legal document')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, documentType, locale, systemId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}

// ============================================================================
// Imperative API (without hooks)
// ============================================================================

/**
 * Fetch the currently effective legal document imperatively (without a hook).
 */
export function fetchCurrentLegalDocument(
  vars: CurrentLegalDocumentVars
): Promise<LegalDocumentGraphQL | null> {
  return fetchQuery<useLegalDocumentQueriesCurrentQuery>(
    relayEnvironment,
    CurrentLegalDocumentQuery,
    vars
  )
    .toPromise()
    .then((result) => (result?.currentLegalDocument as LegalDocumentGraphQL) ?? null);
}

/**
 * Convenience helper that resolves a system's current Terms of Service text.
 *
 * Mirrors the legacy REST `getSystemTOS` contract: resolves to the document's
 * HTML string, or an empty string when no document has been published yet.
 */
export function fetchSystemTosText(systemId: string, locale = 'en'): Promise<string> {
  if (!systemId) return Promise.resolve('');
  return fetchCurrentLegalDocument({
    documentType: 'tos',
    locale,
    systemId,
  }).then((doc) => doc?.textContent ?? '');
}
